import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { FaDownload, FaTimes, FaFilter, FaEye } from "react-icons/fa";
import { GrPowerReset } from "react-icons/gr";
import {
  fetchProjectsByOrg,
  fetchProjectsByTeamMember,
  fetchProjectDetails,
  updateProject,
} from "../Api/ViewProjects";
import { getTeamsByOrg } from "../Api/TeamMaster";
import { getAllProjectStatus } from "../Api/ProjectStatusApi";
import { fetchCustomerMasterByOrgId } from "../Api/ViewCustomerMaster";
import { fetchProjectTypes, fetchResourceByOrgId } from "../Api/InvoiceApi";
import { getProjectMetaNames } from "../Api/ProjectMaster";
import "./ViewProject.css";

// ============================================================
// Utility Functions
// ============================================================
const formatWithId = (name, id) => {
  if (!name && !id) return "";
  if (name && id) return `${name} (${id})`;
  return name || id;
};

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "40px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(59,130,246,0.1)" : "none",
    "&:hover": { borderColor: "#3b82f6" },
    backgroundColor: "#ffffff",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#eff6ff",
    borderRadius: "6px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#1e40af",
    fontSize: "12px",
    fontWeight: 500,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#3b82f6",
    "&:hover": {
      backgroundColor: "#bfdbfe",
      color: "#1e3a8a",
      borderRadius: "4px",
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// ============================================================
// Main Component
// ============================================================
const ViewProjects = () => {
  const navigate = useNavigate();
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = String(userDetails?.userId);
  const orgId = String(userDetails?.organisationId);
  const roles = userDetails?.roles || [];
  const isAdmin = roles.includes("Admin");

  // ========== State ==========
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // Meta Data
  const [metaDataMap, setMetaDataMap] = useState({
    type: {},
    status: {},
    substatus: {},
  });
  const [isMetaDataLoaded, setIsMetaDataLoaded] = useState(false);

  // Master Data
  const [customers, setCustomers] = useState([]);
  const [customerMap, setCustomerMap] = useState({});
  const [projectTypes, setProjectTypes] = useState([]);
  const [projectTypeMap, setProjectTypeMap] = useState({});
  const [teams, setTeams] = useState([]);
  const [teamMap, setTeamMap] = useState({});
  const [resources, setResources] = useState([]);
  const [resourceMap, setResourceMap] = useState({});
  const [projectStatusList, setProjectStatusList] = useState([]);

  // Filter States - All filters displayed directly
  const [globalSearch, setGlobalSearch] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState([]);
  const [selectedSubStatusFilter, setSelectedSubStatusFilter] = useState([]);
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState([]);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState([]);
  const [selectedSalespersonFilter, setSelectedSalespersonFilter] = useState(
    [],
  );
  const [selectedProjectManagerFilter, setSelectedProjectManagerFilter] =
    useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");

  // UI State
  const [showFilters, setShowFilters] = useState(true);

  // Column Order
  const defaultColumnOrder = [
    "index",
    "projectId",
    "title",
    "description",
    "startDate",
    "endDate",
    "client",
    "type",
    "status",
    "subStatus",
    "category",
    "salesperson",
    "projectManager",
  ];
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem("vp2_columnOrder");
    const savedOrder = saved ? JSON.parse(saved) : [];
    return [
      ...defaultColumnOrder.filter((col) => !savedOrder.includes(col)),
      ...savedOrder,
    ];
  });

  const [projectData, setProjectData] = useState({
    prTitle: "",
    prDescription: "",
    prTags: "",
    prStartDate: "",
    prEndDate: "",
    prClient: "",
    prTypes: "",
    teamAssigned: "",
    projectStatus: "",
    projectCategory: "",
    comments: "",
    salespersonCode: "",
    currentProjectManager: "",
  });

  // ========== Helper Functions ==========
  const getDisplayNameFromGuid = useCallback(
    (guid, entityType) => {
      if (!guid) return "—";
      const key = String(guid).toLowerCase();
      return metaDataMap[entityType]?.[key] || "—";
    },
    [metaDataMap],
  );

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return <span className="vp2-sort-icon">↕</span>;
    return sortConfig.direction === "asc" ? (
      <span className="vp2-sort-icon">↑</span>
    ) : (
      <span className="vp2-sort-icon">↓</span>
    );
  };

  // ========== Data Fetching ==========
  const fetchMetaNames = async (projectsList) => {
    if (!projectsList?.length) return;
    try {
      const typeGuids = [
        ...new Set(projectsList.map((p) => p.prTypes).filter(Boolean)),
      ].join(",");
      const statusGuids = [
        ...new Set(projectsList.map((p) => p.projectStatus).filter(Boolean)),
      ].join(",");
      const subStatusGuids = [
        ...new Set(projectsList.map((p) => p.projectSubStatus).filter(Boolean)),
      ].join(",");
      const response = await getProjectMetaNames(
        typeGuids,
        statusGuids,
        subStatusGuids,
      );
      const typeMap = {},
        statusMap = {},
        substatusMap = {};
      response.forEach((item) => {
        const key = item.guid.toLowerCase();
        if (item.entity === "TYPE") typeMap[key] = item.name;
        if (item.entity === "STATUS") statusMap[key] = item.name;
        if (item.entity === "SUBSTATUS") substatusMap[key] = item.name;
      });
      setMetaDataMap({
        type: typeMap,
        status: statusMap,
        substatus: substatusMap,
      });
    } catch (err) {
      console.error("Meta fetch error:", err);
    } finally {
      setIsMetaDataLoaded(true);
    }
  };

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setProjectsLoading(true);
        const data = isAdmin
          ? await fetchProjectsByOrg(orgId)
          : await fetchProjectsByTeamMember(orgId, userId);
        setProjects(data || []);
        await fetchMetaNames(data);
      } catch (error) {
        toast.error("Failed to fetch projects");
      } finally {
        setProjectsLoading(false);
      }
    };
    if (orgId && userId) loadProjects();
  }, [orgId, userId, isAdmin]);

  useEffect(() => {
    const loadResources = async () => {
      try {
        const data = await fetchResourceByOrgId(orgId);
        setResources(data || []);
        setResourceMap(
          Object.fromEntries(data.map((r) => [r.resourceId, r.name])),
        );
      } catch (error) {
        toast.error("Failed to load resources");
      }
    };
    if (orgId) loadResources();
  }, [orgId]);

  useEffect(() => {
    const loadProjectStatus = async () => {
      try {
        const data = await getAllProjectStatus();
        const activeStatus = data.filter((s) => s.active);
        setProjectStatusList(activeStatus);
      } catch (error) {
        toast.error("Failed to load statuses");
      }
    };
    loadProjectStatus();
  }, []);

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const data = await fetchCustomerMasterByOrgId(orgId);
        setCustomers(data || []);
        setCustomerMap(
          Object.fromEntries(data.map((c) => [c.customerCode, c.name])),
        );
      } catch (error) {
        toast.error("Failed to load customers");
      }
    };
    if (orgId) loadCustomers();
  }, [orgId]);

  useEffect(() => {
    const loadProjectTypes = async () => {
      try {
        const data = await fetchProjectTypes(orgId);
        const activeTypes = data.filter((t) => t.active);
        setProjectTypes(activeTypes);
        setProjectTypeMap(
          Object.fromEntries(
            activeTypes.map((t) => [t.projectTypeId, t.projectTypeDesc]),
          ),
        );
      } catch (error) {
        toast.error("Failed to load project types");
      }
    };
    if (orgId) loadProjectTypes();
  }, [orgId]);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const data = await getTeamsByOrg(orgId);
        setTeams(data || []);
        setTeamMap(Object.fromEntries(data.map((t) => [t.teamId, t.teamName])));
      } catch (error) {
        toast.error("Failed to load teams");
      }
    };
    if (orgId) loadTeams();
  }, [orgId]);

  // ========== Derived Data ==========
  const statusOptions = useMemo(() => {
    const unique = new Map();
    Object.entries(metaDataMap.status).forEach(([guid, name]) => {
      if (!unique.has(name) && name !== guid) unique.set(name, guid);
    });
    return Array.from(unique.entries())
      .map(([name, guid]) => ({ value: guid, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [metaDataMap.status]);

  const subStatusOptions = useMemo(() => {
    const unique = new Map();
    Object.entries(metaDataMap.substatus).forEach(([guid, name]) => {
      if (!unique.has(name) && name !== guid) unique.set(name, guid);
    });
    return Array.from(unique.entries())
      .map(([name, guid]) => ({ value: guid, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [metaDataMap.substatus]);

  const typeOptions = useMemo(() => {
    const unique = new Map();
    Object.entries(metaDataMap.type).forEach(([guid, name]) => {
      if (!unique.has(name) && name !== guid) unique.set(name, guid);
    });
    return Array.from(unique.entries())
      .map(([name, guid]) => ({ value: guid, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [metaDataMap.type]);

  const customerOptions = useMemo(
    () =>
      customers
        .map((c) => ({ value: c.customerCode, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [customers],
  );
  const resourceOptions = useMemo(
    () =>
      resources
        .map((r) => ({ value: r.resourceId, label: r.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [resources],
  );

  // Filtered Projects - Combined search for Project ID and Title + all other filters
  const filteredProjects = useMemo(() => {
    return projects
      .filter((project) => {
        // Combined search: matches either Project ID or Title
        const searchMatch = globalSearch
          ? String(project.projectId)
              .toLowerCase()
              .includes(globalSearch.toLowerCase()) ||
            project.prTitle?.toLowerCase().includes(globalSearch.toLowerCase())
          : true;

        if (!searchMatch) return false;
        if (
          selectedStatusFilter.length &&
          !selectedStatusFilter.some((s) => s.value === project.projectStatus)
        )
          return false;
        if (
          selectedSubStatusFilter.length &&
          !selectedSubStatusFilter.some(
            (ss) => ss.value === project.projectSubStatus,
          )
        )
          return false;
        if (
          selectedCustomerFilter.length &&
          !selectedCustomerFilter.some((c) => c.value === project.prClient)
        )
          return false;
        if (
          selectedTypeFilter.length &&
          !selectedTypeFilter.some((t) => t.value === project.prTypes)
        )
          return false;
        if (
          selectedSalespersonFilter.length &&
          !selectedSalespersonFilter.some(
            (s) => s.value === project.salespersonCode,
          )
        )
          return false;
        if (
          selectedProjectManagerFilter.length &&
          !selectedProjectManagerFilter.some(
            (m) => m.value === project.currentProjectManager,
          )
        )
          return false;
        if (
          selectedCategoryFilter &&
          project.projectCategory !== selectedCategoryFilter
        )
          return false;
        if (
          startDateFilter &&
          new Date(project.prStartDate) < new Date(startDateFilter)
        )
          return false;
        if (
          endDateFilter &&
          new Date(project.prEndDate) > new Date(endDateFilter)
        )
          return false;
        return true;
      })
      .sort((a, b) => {
        if (!sortConfig.key) return 0;
        const valA = a[sortConfig.key]
          ? new Date(a[sortConfig.key]).getTime()
          : 0;
        const valB = b[sortConfig.key]
          ? new Date(b[sortConfig.key]).getTime()
          : 0;
        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      });
  }, [
    projects,
    globalSearch,
    selectedStatusFilter,
    selectedSubStatusFilter,
    selectedCustomerFilter,
    selectedTypeFilter,
    selectedSalespersonFilter,
    selectedProjectManagerFilter,
    selectedCategoryFilter,
    startDateFilter,
    endDateFilter,
    sortConfig,
  ]);

  // ========== Event Handlers ==========
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const XLSX = await import("xlsx");
      const worksheetData = [
        [
          "S.No",
          "Project ID",
          "Title",
          "Description",
          "Start Date",
          "End Date",
          "Client",
          "Type",
          "Status",
          "Sub Status",
          "Category",
          "Salesperson",
          "Project Manager",
        ],
        ...filteredProjects.map((p, i) => [
          i + 1,
          p.projectId,
          p.prTitle,
          p.prDescription,
          p.prStartDate ? moment(p.prStartDate).format("YYYY-MM-DD") : "",
          p.prEndDate ? moment(p.prEndDate).format("YYYY-MM-DD") : "",
          formatWithId(customerMap[p.prClient], p.prClient),
          getDisplayNameFromGuid(p.prTypes, "type"),
          getDisplayNameFromGuid(p.projectStatus, "status"),
          getDisplayNameFromGuid(p.projectSubStatus, "substatus"),
          p.projectCategory || "",
          formatWithId(resourceMap[p.salespersonCode], p.salespersonCode),
          formatWithId(
            resourceMap[p.currentProjectManager],
            p.currentProjectManager,
          ),
        ]),
      ];
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Projects");
      XLSX.writeFile(
        wb,
        `Projects_${orgId}_${moment().format("YYYYMMDD")}.xlsx`,
      );
      toast.success("Downloaded successfully!");
    } catch (error) {
      toast.error("Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleProjectSelect = async (projectId) => {
    setSelectedProjectId(projectId);
    try {
      setIsLoading(true);
      const data = await fetchProjectDetails(projectId);
      setProjectData({
        prTitle: data.prTitle || "",
        prDescription: data.prDescription || "",
        prTags: data.prTags || "",
        prStartDate: data.prStartDate
          ? moment(data.prStartDate).format("YYYY-MM-DD")
          : "",
        prEndDate: data.prEndDate
          ? moment(data.prEndDate).format("YYYY-MM-DD")
          : "",
        prClient: data.prClient || "",
        prTypes: data.prTypes || "",
        teamAssigned: data.teamAssigned || "",
        projectStatus: data.projectStatus || "",
        projectCategory: data.projectCategory || "",
        comments: data.comments || "",
        salespersonCode: data.salespersonCode || "",
        currentProjectManager: data.currentProjectManager || "",
      });
    } catch (error) {
      toast.error("Failed to fetch project details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProjectData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProjectId) return toast.error("Select a project");
    try {
      setIsLoading(true);
      await updateProject(selectedProjectId, projectData);
      toast.success("Project updated!");
      const data = isAdmin
        ? await fetchProjectsByOrg(orgId)
        : await fetchProjectsByTeamMember(orgId, userId);
      setProjects(data || []);
      setShowEditModal(false);
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setIsLoading(false);
    }
  };

  const resetAllFilters = () => {
    setGlobalSearch("");
    setSelectedStatusFilter([]);
    setSelectedSubStatusFilter([]);
    setSelectedCustomerFilter([]);
    setSelectedTypeFilter([]);
    setSelectedSalespersonFilter([]);
    setSelectedProjectManagerFilter([]);
    setSelectedCategoryFilter("");
    setStartDateFilter("");
    setEndDateFilter("");
  };

  // ========== Drag & Drop ==========
  const reorderColumns = (fromIndex, toIndex) => {
    const newOrder = [...columnOrder];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    setColumnOrder(newOrder);
  };

  const DraggableHeader = ({ column, index, children }) => {
    const [{ isDragging }, drag] = useDrag({
      type: "COLUMN",
      item: { index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });
    const [, drop] = useDrop({
      accept: "COLUMN",
      hover: (item) => {
        if (item.index !== index) {
          reorderColumns(item.index, index);
          item.index = index;
        }
      },
    });
    return (
      <th
        ref={(node) => drag(drop(node))}
        className={isDragging ? "vp2-dragging" : ""}
      >
        {children}
      </th>
    );
  };

  // Column Config
  const columnConfig = {
    index: {
      label: "#",
      render: (_, i) => <td key="index">{i + 1}</td>,
      sortable: false,
    },
    projectId: {
      label: "PROJECT ID",
      render: (p) => (
        <td
          key="projectId"
          className="vp2-clickable"
          onClick={() => navigate(`/project/${p.projectId}`)}
        >
          {p.projectId}
        </td>
      ),
      sortable: false,
    },
    title: {
      label: "TITLE",
      render: (p) => (
        <td key="title">
          <strong>{p.prTitle}</strong>
        </td>
      ),
      sortable: false,
    },
    description: {
      label: "DESCRIPTION",
      render: (p) => (
        <td key="description" className="vp2-truncate">
          {p.prDescription}
        </td>
      ),
      sortable: false,
    },
    startDate: {
      label: "START DATE",
      render: (p) => (
        <td key="startDate">
          {p.prStartDate ? moment(p.prStartDate).format("L") : "—"}
        </td>
      ),
      sortable: true,
      sortKey: "prStartDate",
    },
    endDate: {
      label: "END DATE",
      render: (p) => (
        <td key="endDate">
          {p.prEndDate ? moment(p.prEndDate).format("L") : "—"}
        </td>
      ),
      sortable: true,
      sortKey: "prEndDate",
    },
    client: {
      label: "CLIENT",
      render: (p) => (
        <td key="client">
          {formatWithId(customerMap[p.prClient], p.prClient) || "—"}
        </td>
      ),
      sortable: false,
    },
    type: {
      label: "TYPE",
      render: (p) => (
        <td key="type">
          <Badge bg="light" className="vp2-type-badge">
            {getDisplayNameFromGuid(p.prTypes, "type")}
          </Badge>
        </td>
      ),
      sortable: false,
    },
    status: {
      label: "STATUS",
      render: (p) => (
        <td key="status">
          <span
            className={`vp2-status-badge vp2-status-${String(p.projectStatus).toLowerCase()}`}
          >
            {getDisplayNameFromGuid(p.projectStatus, "status")}
          </span>
        </td>
      ),
      sortable: false,
    },
    subStatus: {
      label: "SUB STATUS",
      render: (p) => (
        <td key="subStatus">
          {getDisplayNameFromGuid(p.projectSubStatus, "substatus")}
        </td>
      ),
      sortable: false,
    },
    category: {
      label: "CATEGORY",
      render: (p) => <td key="category">{p.projectCategory || "—"}</td>,
      sortable: false,
    },
    salesperson: {
      label: "SALESPERSON",
      render: (p) => (
        <td key="salesperson">
          {formatWithId(resourceMap[p.salespersonCode], p.salespersonCode)}
        </td>
      ),
      sortable: false,
    },
    projectManager: {
      label: "PROJECT MANAGER",
      render: (p) => (
        <td key="projectManager">
          {formatWithId(
            resourceMap[p.currentProjectManager],
            p.currentProjectManager,
          )}
        </td>
      ),
      sortable: false,
    },
  };

  // Active Filters Display
  const ActiveFilters = () => {
    const filters = [
      ...selectedStatusFilter.map((f) => ({
        label: `Status: ${f.label}`,
        onRemove: () =>
          setSelectedStatusFilter((prev) =>
            prev.filter((s) => s.value !== f.value),
          ),
      })),
      ...selectedSubStatusFilter.map((f) => ({
        label: `Sub Status: ${f.label}`,
        onRemove: () =>
          setSelectedSubStatusFilter((prev) =>
            prev.filter((s) => s.value !== f.value),
          ),
      })),
      ...selectedCustomerFilter.map((f) => ({
        label: `Customer: ${f.label}`,
        onRemove: () =>
          setSelectedCustomerFilter((prev) =>
            prev.filter((c) => c.value !== f.value),
          ),
      })),
      ...selectedTypeFilter.map((f) => ({
        label: `Type: ${f.label}`,
        onRemove: () =>
          setSelectedTypeFilter((prev) =>
            prev.filter((t) => t.value !== f.value),
          ),
      })),
      ...selectedSalespersonFilter.map((f) => ({
        label: `Salesperson: ${f.label}`,
        onRemove: () =>
          setSelectedSalespersonFilter((prev) =>
            prev.filter((s) => s.value !== f.value),
          ),
      })),
      ...selectedProjectManagerFilter.map((f) => ({
        label: `Manager: ${f.label}`,
        onRemove: () =>
          setSelectedProjectManagerFilter((prev) =>
            prev.filter((m) => m.value !== f.value),
          ),
      })),
    ];
    if (
      filters.length === 0 &&
      !globalSearch &&
      !selectedCategoryFilter &&
      !startDateFilter &&
      !endDateFilter
    )
      return null;
    return (
      <div className="vp2-active-filters-bar">
        <FaFilter className="vp2-filter-icon" />
        <div className="vp2-filter-chips">
          {globalSearch && (
            <Badge className="vp2-filter-chip">
              Search: "{globalSearch}"{" "}
              <FaTimes onClick={() => setGlobalSearch("")} />
            </Badge>
          )}
          {selectedCategoryFilter && (
            <Badge className="vp2-filter-chip">
              Category: {selectedCategoryFilter}{" "}
              <FaTimes onClick={() => setSelectedCategoryFilter("")} />
            </Badge>
          )}
          {startDateFilter && (
            <Badge className="vp2-filter-chip">
              Start ≥ {moment(startDateFilter).format("L")}{" "}
              <FaTimes onClick={() => setStartDateFilter("")} />
            </Badge>
          )}
          {endDateFilter && (
            <Badge className="vp2-filter-chip">
              End ≤ {moment(endDateFilter).format("L")}{" "}
              <FaTimes onClick={() => setEndDateFilter("")} />
            </Badge>
          )}
          {filters.map((f, i) => (
            <Badge key={i} className="vp2-filter-chip">
              {f.label} <FaTimes onClick={f.onRemove} />
            </Badge>
          ))}
        </div>
        <Button
          variant="link"
          className="vp2-clear-all-btn"
          onClick={resetAllFilters}
        >
          Clear All
        </Button>
      </div>
    );
  };

  // Persist preferences
  useEffect(() => {
    localStorage.setItem("vp2_columnOrder", JSON.stringify(columnOrder));
  }, [columnOrder]);

  if (!isMetaDataLoaded) {
    return (
      <div className="vp2-loading-container">
        <Spinner animation="border" variant="primary" />
        <span>Loading project data...</span>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="vp2-view-projects-container">
        <Container fluid className="vp2-px-4 vp2-py-4">
          {/* Header */}
          <div className="vp2-page-header">
            <div>
              <p className="vp2-page-subtitle">Workspace / Active Projects</p>
              <h1 className="vp2-page-title">View Project</h1>
            </div>
            <Button
              style={{ borderRadius: "15px" }}
              className="vp2-download-btn"
              onClick={handleDownload}
              disabled={isDownloading}
            >
              <FaDownload /> {isDownloading ? "Downloading..." : "Download"}
            </Button>
          </div>

          {/* Smart Filters Panel */}
          <div className="vp2-filters-panel">
            <div
              className="vp2-filters-header"
              onClick={() => setShowFilters(!showFilters)}
            >
              <div className="vp2-filters-title">
                <span className="vp2-filters-title-icon">
                  <FaEye className="vp2-title-icon" />
                </span>
                <span>Smart Filters</span>
              </div>
              {/* Global Search Row */}
              <div className="vp2-filter-row">
                <div className="vp2-filter-group vp2-search-group vp2-full-width">
                  <input
                    style={{ height: "3.5rem" }}
                    type="text"
                    className="vp2-search-input"
                    placeholder="Search by Project ID or Title..."
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                  />
                </div>
              </div>
              <span
                className={`vp2-toggle-icon ${showFilters ? "vp2-rotated" : ""}`}
              >
                ▼
              </span>
            </div>

            {showFilters && (
              <div className="vp2-filters-body">
                {/* Global Search Row
                <div className="vp2-filter-row">
                  <div className="vp2-filter-group vp2-search-group vp2-full-width">
                    <input 
                      type="text" 
                      className="vp2-search-input" 
                      placeholder="Search by Project ID or Title..." 
                      value={globalSearch} 
                      onChange={(e) => setGlobalSearch(e.target.value)} 
                    />
                  </div>
                </div> */}

                {/* All Filters Grid - All filters displayed directly */}
                <div className="vp2-filter-grid">
                  {/* Status Filter */}
                  <div className="vp2-filter-group">
                    <label>Status</label>
                    <Select
                      isMulti
                      options={statusOptions}
                      value={selectedStatusFilter}
                      onChange={setSelectedStatusFilter}
                      styles={customSelectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Select status..."
                    />
                  </div>

                  {/* Sub Status Filter */}
                  <div className="vp2-filter-group">
                    <label>Sub Status</label>
                    <Select
                      isMulti
                      options={subStatusOptions}
                      value={selectedSubStatusFilter}
                      onChange={setSelectedSubStatusFilter}
                      styles={customSelectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Select sub status..."
                    />
                  </div>

                  {/* Customer Filter */}
                  <div className="vp2-filter-group">
                    <label>Customer</label>
                    <Select
                      isMulti
                      options={customerOptions}
                      value={selectedCustomerFilter}
                      onChange={setSelectedCustomerFilter}
                      styles={customSelectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Select customers..."
                    />
                  </div>

                  {/* Project Type Filter */}
                  <div className="vp2-filter-group">
                    <label>Project Type</label>
                    <Select
                      isMulti
                      options={typeOptions}
                      value={selectedTypeFilter}
                      onChange={setSelectedTypeFilter}
                      styles={customSelectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Select types..."
                    />
                  </div>

                  {/* Category Filter */}
                  <div className="vp2-filter-group">
                    <label>Category</label>
                    <select
                      style={{ height: "3.7rem" }}
                      value={selectedCategoryFilter}
                      onChange={(e) =>
                        setSelectedCategoryFilter(e.target.value)
                      }
                    >
                      <option value="">All</option>
                      <option value="Internal">Internal</option>
                      <option value="External">External</option>
                    </select>
                  </div>

                  {/* Salesperson Filter */}
                  <div className="vp2-filter-group">
                    <label>Salesperson</label>
                    <Select
                      isMulti
                      options={resourceOptions}
                      value={selectedSalespersonFilter}
                      onChange={setSelectedSalespersonFilter}
                      styles={customSelectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Select salespersons..."
                    />
                  </div>

                  {/* Project Manager Filter */}
                  <div className="vp2-filter-group">
                    <label>Project Manager</label>
                    <Select
                      isMulti
                      options={resourceOptions}
                      value={selectedProjectManagerFilter}
                      onChange={setSelectedProjectManagerFilter}
                      styles={customSelectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Select managers..."
                    />
                  </div>

                  {/* Start Date Filter */}
                  <div className="vp2-filter-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={startDateFilter}
                      onChange={(e) => setStartDateFilter(e.target.value)}
                    />
                  </div>

                  {/* End Date Filter */}
                  <div className="vp2-filter-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={endDateFilter}
                      onChange={(e) => setEndDateFilter(e.target.value)}
                    />
                  </div>
                </div>

                {/* Reset Button */}
                <div className="vp2-filters-actions">
                  <Button
                    variant="link"
                    className="vp2-reset-btn"
                    onClick={resetAllFilters}
                  >
                    <GrPowerReset /> Reset All Filters
                  </Button>
                </div>
              </div>
            )}
          </div>

          <ActiveFilters />

          {/* Table */}
          {projectsLoading ? (
            <div className="vp2-loading-container">
              <Spinner animation="border" variant="primary" />
              <span>Loading projects...</span>
            </div>
          ) : filteredProjects.length > 0 ? (
            <div className="vp2-table-wrapper">
              <div className="vp2-table-scroll">
                <table className="vp2-data-table">
                  <thead>
                    <tr>
                      {columnOrder.map((colKey, idx) => {
                        const col = columnConfig[colKey];
                        if (!col) return null;
                        return (
                          <DraggableHeader
                            key={colKey}
                            column={colKey}
                            index={idx}
                          >
                            {col.sortable ? (
                              <div
                                className="vp2-sortable-header"
                                onClick={() => handleSort(col.sortKey)}
                              >
                                {col.label} {renderSortArrow(col.sortKey)}
                              </div>
                            ) : (
                              col.label
                            )}
                          </DraggableHeader>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((project, idx) => (
                      <tr key={project.projectId || idx}>
                        {columnOrder.map((colKey) => {
                          const col = columnConfig[colKey];
                          return col ? col.render(project, idx) : null;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="vp2-no-data">No projects found.</div>
          )}
        </Container>

        {/* Edit Modal */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Project</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Project ID</Form.Label>
                <Form.Select
                  value={selectedProjectId}
                  onChange={(e) => handleProjectSelect(e.target.value)}
                  required
                >
                  <option value="">Select project</option>
                  {projects.map((p) => (
                    <option key={p.projectId} value={p.projectId}>
                      {p.projectId} - {p.prTitle}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      name="prTitle"
                      value={projectData.prTitle}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="prDescription"
                      value={projectData.prDescription}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="prStartDate"
                      value={projectData.prStartDate}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="prEndDate"
                      value={projectData.prEndDate}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Client</Form.Label>
                    <Form.Control
                      name="prClient"
                      value={projectData.prClient}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      name="prTypes"
                      value={projectData.prTypes}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      {projectTypes.map((t) => (
                        <option key={t.projectTypeId} value={t.projectTypeId}>
                          {t.projectTypeDesc}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="projectStatus"
                      value={projectData.projectStatus}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      {projectStatusList.map((s) => (
                        <option
                          key={s.projectStatusId}
                          value={s.projectStatusId}
                        >
                          {s.projectStatusDesc}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Category</Form.Label>
                    <Form.Select
                      name="projectCategory"
                      value={projectData.projectCategory}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      <option value="Internal">Internal</option>
                      <option value="External">External</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Salesperson</Form.Label>
                    <Form.Control
                      name="salespersonCode"
                      value={projectData.salespersonCode}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Project Manager</Form.Label>
                    <Form.Control
                      name="currentProjectManager"
                      value={projectData.currentProjectManager}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isLoading}>
                  {isLoading ? "Updating..." : "Update"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </DndProvider>
  );
};

export default ViewProjects;
